import { createContext, type FC, type ReactNode, useContext } from 'react';

export type ServerVarsContextType = {
    isBot?: boolean; // should be set only for SSR
    isBotDetected?: boolean; // true if NGWAF bot detected
    isDesktop?: boolean | null;
    isMobile?: boolean | null;
    areDisplayAdsEnabled?: boolean | null;
    isTablet?: boolean | null;
    clientConf?: { [key: string]: string | number | undefined } | null;
    localeOrigin?: string | null;
    isSearchQueryApplied?: boolean | null;
    locale?: string | null;
    originalUrl?: string | null;
    isTradeQuickFiltersVariant?: boolean;
    isPdpMwLayoutShiftFixVariant?: boolean;
    isTableSizeFilterVariant?: boolean;
    urgencySignalsMwVariant?: string;
    isContentVisibilitySbVariant?: boolean;
    isPdpRespPreviewPage?: boolean;
    isBannerAdsAbTestVariant?: boolean;
    isShipsFromSBTileVariant?: boolean;
    pdpMwUxRedesignVariant?: string | null;
    pdpTopYmalSponsoredTestVariant?: string;
    isItemTileRedesignTestVariant?: boolean;
    isPdpSellerReviewsVariant?: boolean;
    isPdpEstimateSimilarSoldTestVariant?: boolean;
    isHolidayGiftCollectionPage?: boolean;
};

export const ServerVarsContext = createContext<ServerVarsContextType>({
    isBot: false,
    isBotDetected: false,
    isDesktop: null,
    isMobile: null,
    areDisplayAdsEnabled: null,
    isTablet: null,
    clientConf: {},
    localeOrigin: null,
    locale: null,
    originalUrl: null,
    isSearchQueryApplied: null,
    isTradeQuickFiltersVariant: false,
    isPdpMwLayoutShiftFixVariant: false,
    isTableSizeFilterVariant: false,
    urgencySignalsMwVariant: undefined, // if test is off, "urgencySignalsMwVariant" should not have value
    isContentVisibilitySbVariant: false,
    isPdpRespPreviewPage: false,
    isBannerAdsAbTestVariant: false,
    isShipsFromSBTileVariant: false,
    pdpMwUxRedesignVariant: null,
    pdpTopYmalSponsoredTestVariant: undefined,
    isItemTileRedesignTestVariant: false,
    isPdpSellerReviewsVariant: false,
    isPdpEstimateSimilarSoldTestVariant: false,
    isHolidayGiftCollectionPage: false,
});

export const useServerVarsContext = (): ServerVarsContextType =>
    useContext<ServerVarsContextType>(ServerVarsContext);

export const ServerVarsContextConsumer = ServerVarsContext.Consumer;

export const ServerVarsContextProvider: FC<ServerVarsContextType & { children: ReactNode }> = ({
    children,
    isDesktop,
    isMobile,
    areDisplayAdsEnabled,
    isTablet,
    clientConf,
    localeOrigin,
    originalUrl,
    isSearchQueryApplied,
    locale,
    isBot,
    isBotDetected,
    isTradeQuickFiltersVariant,
    isPdpMwLayoutShiftFixVariant,
    isTableSizeFilterVariant,
    urgencySignalsMwVariant,
    isContentVisibilitySbVariant,
    isPdpRespPreviewPage,
    isBannerAdsAbTestVariant,
    isShipsFromSBTileVariant,
    pdpMwUxRedesignVariant,
    pdpTopYmalSponsoredTestVariant,
    isItemTileRedesignTestVariant,
    isPdpSellerReviewsVariant,
    isPdpEstimateSimilarSoldTestVariant,
    isHolidayGiftCollectionPage,
}) => {
    return (
        <ServerVarsContext.Provider
            value={{
                isDesktop,
                isMobile,
                areDisplayAdsEnabled,
                isTablet,
                clientConf,
                localeOrigin,
                originalUrl,
                isSearchQueryApplied,
                locale,
                isBot,
                isBotDetected,
                isTradeQuickFiltersVariant,
                isPdpMwLayoutShiftFixVariant,
                isTableSizeFilterVariant,
                urgencySignalsMwVariant,
                isContentVisibilitySbVariant,
                isPdpRespPreviewPage,
                isBannerAdsAbTestVariant,
                isShipsFromSBTileVariant,
                pdpMwUxRedesignVariant,
                pdpTopYmalSponsoredTestVariant,
                isItemTileRedesignTestVariant,
                isPdpSellerReviewsVariant,
                isPdpEstimateSimilarSoldTestVariant,
                isHolidayGiftCollectionPage,
            }}
        >
            {children}
        </ServerVarsContext.Provider>
    );
};
